import '../../styles/restScreens.css';
import { LIFECYCLE_STEPS, STATUS_META, statusMeta, isSideExit, allowedNextStatuses } from './engagementConstants';

// A horizontal step-by-step bar showing where an engagement sits in its lifecycle:
// Requested → Confirmed → Scheduled → In Progress → Completed → Closed.
// Canceled / No-Show are side-exits: the main path is dimmed and a red terminal chip is shown.
//
// "Closed doesn't turn green" (Val) — and it never could. `isDone` was `idx < currentIndex`:
// a step goes green once you PASS it, and nothing comes after Closed, so the final step
// failed that test forever and rendered in the orange "you are here" state.
//
// That mattered more than it looked. Val has since defined it: CLOSED = payment received and
// fully reconciled. So the one status meaning "we got our money" — the most positive thing
// that can happen to an engagement — was the one status that could never render as positive.
//
// Fixed by asking the lifecycle rules instead of counting: a status with NO legal exits is
// done by definition. That comes from ALLOWED_TRANSITIONS, which mirrors
// validate_engagement_status_transition() (migrations 005/021), so the bar agrees with the
// database rather than with an index.
function LifecycleBar({ status, theme = 'light' }) {
    const dark = theme === 'dark';
    const sideExit = isSideExit(status);
    const currentIndex = LIFECYCLE_STEPS.indexOf(status);

    return (
        <div data-m6-layout="engagements" data-m6-component="LifecycleBar" className="w-full">
            <div className={`flex items-center ${sideExit ? 'opacity-40' : ''}`}>
                {LIFECYCLE_STEPS.map((step, idx) => {
                    const meta = STATUS_META[step];
                    // A status you cannot leave is finished — you are not "at" Closed, you
                    // are done. Derived from the DB's transition rules, not from being last.
                    const isTerminal = allowedNextStatuses(step).length === 0;
                    const reached = !sideExit && idx === currentIndex;
                    const isDone = (!sideExit && idx < currentIndex) || (reached && isTerminal);
                    const isCurrent = reached && !isTerminal;
                    const isLast = idx === LIFECYCLE_STEPS.length - 1;

                    // Circle styling by state
                    let circleClasses;
                    if (isCurrent) {
                        circleClasses = 'bg-[var(--ui-orange)] text-white border-[var(--ui-orange)] ring-4 ring-[var(--ui-orange)]/30';
                    } else if (isDone) {
                        circleClasses = 'bg-[var(--ui-positive)] text-white border-[var(--ui-positive)]';
                    } else {
                        circleClasses = dark
                            ? 'bg-gray-700 text-gray-400 border-gray-600'
                            : 'bg-white text-gray-400 border-gray-300';
                    }

                    const labelClasses = isCurrent
                        ? 'text-[var(--ui-orange)] font-bold'
                        : isDone
                            ? 'text-[var(--ui-positive)] font-medium'
                            : (dark ? 'text-gray-500' : 'text-gray-400');

                    return (
                        <div key={step} className="flex items-center flex-1 last:flex-none">
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm transition-all ${circleClasses}`}
                                    title={meta.label}
                                >
                                    {isDone ? '✓' : idx + 1}
                                </div>
                                <span className={`mt-1 text-[11px] whitespace-nowrap ${labelClasses}`}>
                                    {meta.label}
                                </span>
                            </div>
                            {!isLast && (
                                <div
                                    className={`flex-1 h-0.5 mx-1 mb-5 ${
                                        isDone ? 'bg-[var(--ui-positive)]' : (dark ? 'bg-gray-700' : 'bg-gray-200')
                                    }`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {sideExit && (
                <div className="mt-3 flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold border ${statusMeta(status).pill}`}>
                        {statusMeta(status).icon} {statusMeta(status).label}
                    </span>
                    <span className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                        This engagement left the main path and is now closed to further steps.
                    </span>
                </div>
            )}
        </div>
    );
}

export default LifecycleBar;
